// @ts-nocheck
import classNames from "classnames";
import { styled } from "@stitches/react";
import {
  TLBaseShape,
  TLShapeUtilCanvasSvgDef,
  RecordProps,
  T,
  BoundsSnapGeometry,
  Rectangle2d,
  HTMLContainer,
  BaseBoxShapeUtil,
} from "tldraw";
import { Utils } from "@tldraw/core";
import {
  Editor,
  FileHelpers,
  MediaHelpers,
  TLAssetId,
  TLImageAsset,
  TLImageShape,
  TLImageShapeProps,
  TLResizeInfo,
  TLShapePartial,
  TLVideoAsset,
  Vec,
  debounce,
  imageShapeMigrations,
  imageShapeProps,
  lerp,
  resizeBox,
  structuredClone,
  toDomPrecision,
  useDelaySvgExport,
  useEditor,
  useSvgExportContext,
  useValue,
  useUniqueSafeId,
} from "@tldraw/editor";
import { BINDING_DISTANCE } from "pages/flyer/builder/drawer/constants/constants";
import {
  defaultImageStyle,
  getBoundsRectangle,
  transformRectangle,
  transformSingleRectangle,
} from "pages/flyer/builder/drawer/state/shapes/shared";
import { TDImageAsset, TDMeta, TDShapeType, StaticAssetsIdsEnum } from "pages/flyer/builder/drawer/types";
import * as React from "react";
import { TDShapeUtil } from "../TDShapeUtil";
import { readS3Marker } from "pages/flyer/builder/utils/s3Marker";

// type T = ImageShape;
type E = HTMLDivElement;

export class ImageUtil extends BaseBoxShapeUtil<E> {
  type = TDShapeType.Image as const;

  canBind = true;
  canClone = true;
  showCloneHandles = true;

  getDefaultProps = () => {
    return {
      id: "image",
      type: TDShapeType.Image,
      parentId: "page",
      rotation: 0,
      assetId: "assetId",
      w: 500,
      h: 500,
      props: {
        name: "Image",
        childIndex: 1,
        point: [0, 0],
        size: [1, 1],
        style: defaultImageStyle,
      },
    };

    // getShape = (props: Partial<T>): T => {
    //   return Utils.deepMerge<T>(
    //     {
    //       id: "image",
    //       type: TDShapeType.Image,
    //       name: "Image",
    //       parentId: "page",
    //       childIndex: 1,
    //       point: [0, 0],
    //       size: [1, 1],
    //       rotation: 0,
    //       style: defaultImageStyle,
    //       assetId: "assetId",
    //     },
    //     props
    //   );
    // };

    Component = TDShapeUtil.Component<T, E, TDMeta>(
      (
        {
          shape,
          asset = { src: "" },
          isBinding,
          isGhost,
          meta,
          events,
          isSelected,
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          onShapeChange,
        },
        ref
      ) => {
        const { size, style, id } = shape;
        const {
          strokeColor: stroke,
          fillColor: fill,
          transparency,
          rounded,
          strokeWidth,
          isStroked,
          isFilled,
          isOpacityVisible,
          isRoundingVisible,
          isInnerShadowVisible,
          isOuterShadowVisible,
          innerShadowColor,
          innerShadow,
          outerShadow,
          outerShadowColor,
        } = style;
        const sw = strokeWidth!;
        const isRoundingEnable = false;

        const w = Math.max(0, size[0]);
        const h = Math.max(0, size[1]);
        const rImage = React.useRef<HTMLImageElement>(null);
        const rWrapper = React.useRef<HTMLDivElement>(null);

        React.useLayoutEffect(() => {
          const wrapper = rWrapper.current;
          if (!wrapper) return;
          wrapper.style.width = `${w}px`;
          wrapper.style.height = `${h}px`;

          const image = rImage.current;
          if (!image) return;
          image.style.width = `${w}px`;
          image.style.height = `${h}px`;
          // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [size, sw]);

        if (
          (
            [
              StaticAssetsIdsEnum.CutLineFront,
              StaticAssetsIdsEnum.CutLineBack,
              StaticAssetsIdsEnum.DiecutLineFront,
              StaticAssetsIdsEnum.DiecutLineBack,
              StaticAssetsIdsEnum.TearawayCutLineFront,
              StaticAssetsIdsEnum.TearawayCutLineBack,
            ] as string[]
          ).includes(shape.id)
        )
          return <></>;

        return (
          <HTMLContainer ref={ref} {...events}>
            {isBinding && (
              <div
                className="tl-binding-indicator"
                style={{
                  position: "absolute",
                  top: `calc(${-this.bindingDistance}px * var(--tl-zoom))`,
                  left: `calc(${-this.bindingDistance}px * var(--tl-zoom))`,
                  width: `calc(100% + ${this.bindingDistance * 2}px * var(--tl-zoom))`,
                  height: `calc(100% + ${this.bindingDistance * 2}px * var(--tl-zoom))`,
                  backgroundColor: "var(--tl-selectFill)",
                }}
              />
            )}
            <Wrapper
              ref={rWrapper}
              isDarkMode={meta.isDarkMode} //
              isFilled={style.isFilled}
              isGhost={isGhost}
              style={{
                width: `calc(100% - ${style.strokeWidth}px)`,
                height: `calc(100% - ${style.strokeWidth}px)`,
              }}
            >
              <svg
                width={w}
                height={h}
                // style={{
                //   pointerEvents: isGhost ? "none" : "auto",
                // }}
              >
                <defs>
                  {outerShadow && isOuterShadowVisible ? (
                    <filter id={`shadow_${id}`} x="-400%" y="-400%" height="1000%" width="1000%">
                      <feGaussianBlur in="SourceGraphic" stdDeviation={outerShadow.blur} />
                    </filter>
                  ) : null}
                  {innerShadow && isInnerShadowVisible ? (
                    <filter id={`inset-shadow_${id}`} x="-50%" y="-50%" width="200%" height="200%">
                      <feComponentTransfer in="SourceAlpha">
                        <feFuncA type="table" tableValues="1 0" />
                      </feComponentTransfer>
                      <feGaussianBlur stdDeviation={innerShadow.blur} />
                      <feMorphology operator="dilate" radius={innerShadow.spread} />

                      <feOffset dx={innerShadow.x} dy={innerShadow.y} result="offsetblur" />
                      <feFlood floodColor={innerShadowColor} result="color" />
                      <feComposite in2="offsetblur" operator="in" />
                      <feComposite in2="SourceAlpha" operator="in" />
                      <feMerge>
                        <feMergeNode in="SourceGraphic" />
                        <feMergeNode />
                      </feMerge>
                    </filter>
                  ) : null}
                </defs>

                {outerShadow && isOuterShadowVisible ? (
                  <rect
                    x={outerShadow?.x - outerShadow.spread / 2 - sw / 2}
                    y={outerShadow?.y - outerShadow.spread / 2 - sw / 2}
                    width={w + sw + outerShadow.spread}
                    height={h + sw + outerShadow.spread}
                    fill={outerShadowColor}
                    stroke={outerShadowColor}
                    strokeWidth={isStroked ? sw : undefined}
                    pointerEvents="none"
                    opacity={isOpacityVisible ? transparency! / 100 : 1}
                    rx={isRoundingEnable && isRoundingVisible ? rounded : 0}
                    filter={outerShadow && isOuterShadowVisible ? `url(#shadow_${id})` : undefined}
                  />
                ) : null}
                <rect
                  className={isSelected || style.isFilled ? "tl-fill-hitarea" : "tl-stroke-hitarea"}
                  x={-sw / 2}
                  y={-sw / 2}
                  width={w}
                  height={h}
                  strokeWidth={BINDING_DISTANCE}
                  rx={isRoundingEnable ? rounded : undefined}
                  style={{
                    pointerEvents: isGhost ? "none" : "auto",
                  }}
                />
                <rect
                  x={-sw / 2}
                  y={-sw / 2}
                  width={w + sw}
                  height={h + sw}
                  fill={isFilled ? fill : "rgba(0,0,0,0)"}
                  stroke={isStroked ? stroke : undefined}
                  strokeWidth={isStroked ? sw : undefined}
                  pointerEvents="none"
                  opacity={isOpacityVisible ? transparency! / 100 : 1}
                  rx={isRoundingEnable && isRoundingVisible ? rounded : 0}
                />
              </svg>

              <ImageElement
                id={shape.id + "_image"}
                ref={rImage}
                src={(asset as TDImageAsset).src}
                alt="tl_image_asset"
                draggable={false}
                style={{
                  opacity: style.transparency! / 100,
                  width: `calc(100% - ${style.strokeWidth}px)`,
                  height: `calc(100% - ${style.strokeWidth}px)`,
                  pointerEvents: isGhost ? "none" : "auto",
                }}
                // onLoad={onImageLoad}
              />
            </Wrapper>
          </HTMLContainer>
        );
      }
    );

    Indicator = TDShapeUtil.Indicator<T>(({ shape }) => {
      const { size } = shape;

      return <rect width={Math.max(1, size[0])} height={Math.max(1, size[1])} />;
    });

    getBounds = (shape: T) => {
      return getBoundsRectangle(shape, this.boundsCache);
    };

    shouldRender = (prev: T, next: T) => {
      return next.size !== prev.size || next.style !== prev.style;
    };

    transform = transformRectangle;

    transformSingle = transformSingleRectangle;

    // getSvgElement = (shape: ImageShape) => {
    //   const bounds = this.getBounds(shape);
    //   const elm = document.createElementNS("http://www.w3.org/2000/svg", "image");
    //   elm.setAttribute("width", `${bounds.width}`);
    //   elm.setAttribute("height", `${bounds.height}`);
    //   elm.setAttribute("xmlns:xlink", `http://www.w3.org/1999/xlink`);
    //   return elm;
  };

  component(shape: TLImageShape) {
    return <ImageShape shape={shape} />;
  }

  indicator(shape: TLImageShape) {
    const isCropping = this.editor.getCroppingShapeId() === shape.id;
    if (isCropping) return null;
    return <rect width={toDomPrecision(shape.props.w)} height={toDomPrecision(shape.props.h)} />;
  }

  override async toSvg(shape: TLImageShape) {
    if (!shape.props.assetId) return null;

    const asset = this.editor.getAsset(shape.props.assetId);

    if (!asset) return null;

    let src = await this.editor.resolveAssetUrl(shape.props.assetId, {
      shouldResolveToOriginal: true,
    });
    if (!src) return null;
    if (src.startsWith("blob:") || src.startsWith("http") || src.startsWith("/") || src.startsWith("./")) {
      // If it's a remote image, we need to fetch it and convert it to a data URI
      src = (await getDataURIFromURL(src)) || "";
    }

    return <SvgImage shape={shape} src={src} />;
  }
}

const Wrapper = styled("div", {
  pointerEvents: "all",
  position: "relative",
  fontFamily: "sans-serif",
  fontSize: "2em",
  height: "100%",
  width: "100%",
  borderRadius: "3px",
  perspective: "800px",
  // overflow: "hidden",
  p: {
    userSelect: "none",
  },
  img: {
    userSelect: "none",
  },
  variants: {
    isGhost: {
      false: { opacity: 1 },
      // true: { transition: "opacity .2s", opacity: GHOSTED_OPACITY },
      true: { pointerEvents: "none" },
    },
    isFilled: {
      true: {},
      false: {},
    },
    isDarkMode: {
      true: {},
      false: {},
    },
  },
  compoundVariants: [
    {
      isFilled: true,
      isDarkMode: true,
      css: {
        boxShadow: "2px 3px 12px -2px rgba(0,0,0,.3), 1px 1px 4px rgba(0,0,0,.3), 1px 1px 2px rgba(0,0,0,.3)",
      },
    },
  ],
});

const ImageElement = styled("img", {
  position: "absolute",
  top: 0,
  left: 0,
  // width: "100%",
  height: "100%",
  maxWidth: "100%",
  // minWidth: "100%",
  pointerEvents: "none",
  objectFit: "cover",
  userSelect: "none",
  zIndex: 0,
});

const ImageShape = React.memo(function ImageShape({ shape }: { shape: TLImageShape }) {
  const editor = useEditor();

  const { asset, url } = useImageOrVideoAsset({
    shapeId: shape.id,
    assetId: shape.props.assetId,
  });

  const prefersReducedMotion = usePrefersReducedMotion();
  const [staticFrameSrc, setStaticFrameSrc] = React.useState("");
  const [loadedUrl, setLoadedUrl] = React.useState<null | string>(null);

  const isAnimated = getIsAnimated(editor, shape);

  React.useEffect(() => {
    if (url && isAnimated) {
      let cancelled = false;

      const image = new Image();
      image.onload = () => {
        if (cancelled) return;

        const canvas = document.createElement("canvas");
        canvas.width = image.width;
        canvas.height = image.height;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.drawImage(image, 0, 0);
        setStaticFrameSrc(canvas.toDataURL());
        setLoadedUrl(url);
      };
      image.crossOrigin = "anonymous";
      image.src = url;

      return () => {
        cancelled = true;
      };
    }
  }, [editor, isAnimated, prefersReducedMotion, url]);

  const showCropPreview = useValue(
    "show crop preview",
    () =>
      shape.id === editor.getOnlySelectedShapeId() &&
      editor.getCroppingShapeId() === shape.id &&
      editor.isIn("select.crop"),
    [editor, shape.id]
  );

  // We only want to reduce motion for mimeTypes that have motion
  const reduceMotion = prefersReducedMotion && (asset?.props.mimeType?.includes("video") || isAnimated);

  const containerStyle = getCroppedContainerStyle(shape);

  const nextSrc = url === loadedUrl ? null : url;
  const loadedSrc = reduceMotion ? staticFrameSrc : loadedUrl;

  // This logic path is for when it's broken/missing asset.
  if (!url && !asset?.props.src) {
    return (
      <HTMLContainer
        id={shape.id}
        style={{
          overflow: "hidden",
          width: shape.props.w,
          height: shape.props.h,
          color: "var(--color-text-3)",
          backgroundColor: "var(--color-low)",
          border: "1px solid var(--color-low-border)",
        }}
      >
        <div className={classNames("tl-image-container", asset && "tl-image-container-loading")} style={containerStyle}>
          {asset ? null : "BrokenAssetIcon"}
        </div>
        {/* {"url" in shape.props && shape.props.url && <HyperlinkButton url={shape.props.url} />} */}
      </HTMLContainer>
    );
  }

  // We don't set crossOrigin for non-animated images because for Cloudflare we don't currently
  // have that set up.
  const crossOrigin = isAnimated ? "anonymous" : undefined;

  return (
    <>
      {showCropPreview && loadedSrc && (
        <div style={containerStyle}>
          <img
            className="tl-image"
            style={{ ...getFlipStyle(shape), opacity: 0.1 }}
            crossOrigin={crossOrigin}
            src={loadedSrc}
            referrerPolicy="strict-origin-when-cross-origin"
            draggable={false}
          />
        </div>
      )}
      <HTMLContainer id={shape.id} style={{ overflow: "hidden", width: shape.props.w, height: shape.props.h }}>
        <div className={classNames("tl-image-container")} style={containerStyle}>
          {/* We have two images: the currently loaded image, and the next image that
					we're waiting to load. we keep the loaded image mounted while we're waiting
					for the next one by storing the loaded URL in state. We use `key` props with
					the src of the image so that when the next image is ready, the previous one will
					be unmounted and the next will be shown with the browser having to remount a
					fresh image and decoded it again from the cache. */}
          {loadedSrc && (
            <img
              key={loadedSrc}
              className="tl-image"
              style={getFlipStyle(shape)}
              crossOrigin={crossOrigin}
              src={loadedSrc}
              referrerPolicy="strict-origin-when-cross-origin"
              draggable={false}
            />
          )}
          {nextSrc && (
            <img
              key={nextSrc}
              className="tl-image"
              style={getFlipStyle(shape)}
              crossOrigin={crossOrigin}
              src={nextSrc}
              referrerPolicy="strict-origin-when-cross-origin"
              draggable={false}
              onLoad={() => setLoadedUrl(nextSrc)}
            />
          )}
        </div>
        {/* {shape.props.url && <HyperlinkButton url={shape.props.url} />} */}
      </HTMLContainer>
    </>
  );
});

export function useImageOrVideoAsset({ shapeId, assetId }: { shapeId: TLShapeId; assetId: TLAssetId | null }) {
  const editor = useEditor();
  const isExport = !!useSvgExportContext();
  const isReady = useDelaySvgExport();

  // We use a state to store the result of the asset resolution, and we're going to avoid updating this whenever we can
  const [result, setResult] = React.useState<{
    asset: (TLImageAsset | TLVideoAsset) | null;
    url: string | null;
  }>(() => ({
    asset: assetId ? editor.getAsset<TLImageAsset | TLVideoAsset>(assetId) ?? null : null,
    url: null as string | null,
  }));

  // A flag for whether we've resolved the asset URL at least once, after which we can debounce
  const didAlreadyResolve = React.useRef(false);

  // The last URL that we've seen for the shape
  const previousUrl = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!assetId) return;

    let isCancelled = false;
    let cancelDebounceFn: (() => void) | undefined;

    const cleanupEffectScheduler = react("update state", () => {
      if (!isExport && editor.getCulledShapes().has(shapeId)) return;

      // Get the fresh asset
      const asset = editor.getAsset<TLImageAsset | TLVideoAsset>(assetId);
      if (!asset) return;

      // Get the fresh shape
      const shape = editor.getShape<TLImageShape | TLVideoShape>(shapeId);
      if (!shape) return;

      // Set initial preview for the shape if it has no source (if it was pasted into a local project as base64)
      if (!asset.props.src) {
        const preview = editor.getTemporaryAssetPreview(asset.id);
        if (preview) {
          if (previousUrl.current !== preview) {
            previousUrl.current = preview; // just for kicks, let's save the url as the previous URL
            setResult((prev) => ({ ...prev, isPlaceholder: true, url: preview })); // set the preview as the URL
            isReady(); // let the SVG export know we're ready for export
          }
          return;
        }
      }

      // aside ...we could bail here if the only thing that has changed is the shape has changed from culled to not culled

      const screenScale = editor.getZoomLevel() * (shape.props.w / asset.props.w);

      // If we already resolved the URL, debounce fetching potentially multiple image variations.
      if (didAlreadyResolve.current) {
        resolveAssetUrlDebounced(editor, assetId, screenScale, isExport, (url) => {
          if (isCancelled) return; // don't update if the hook has remounted
          if (previousUrl.current === url) return; // don't update the state if the url is the same
          previousUrl.current = url; // keep the url around to compare with the next one
          setResult(() => ({ asset, url }));
        });
        cancelDebounceFn = resolveAssetUrlDebounced.cancel; // cancel the debounce when the hook unmounts
      } else {
        resolveAssetUrl(editor, assetId, screenScale, isExport, (url) => {
          if (isCancelled) return; // don't update if the hook has remounted
          didAlreadyResolve.current = true; // mark that we've resolved our first image
          previousUrl.current = url; // keep the url around to compare with the next one
          setResult(() => ({ asset, url }));
        });
      }
    });

    return () => {
      cleanupEffectScheduler();
      cancelDebounceFn?.();
      isCancelled = true;
    };
  }, [editor, assetId, isExport, isReady, shapeId]);

  return result;
}

function resolveAssetUrl(
  editor: Editor,
  assetId: TLAssetId,
  screenScale: number,
  isExport: boolean,
  callback: (url: string | null) => void
) {
  editor
    .resolveAssetUrl(assetId, {
      screenScale,
      shouldResolveToOriginal: isExport,
    })
    // There's a weird bug with out debounce function that doesn't
    // make it work right with async functions, so we use a callback
    // here instead of returning a promise.
    .then(async (url) => {
      // Check if the URL contains our S3 key marker for re-signing expired URLs
      if (url?.includes("#s3key=") || url?.includes("#s3bucket=")) {
        try {
          const { getSignedUrl } = await import("graphQL/query/file");
          const marker = readS3Marker(url);
          if (!marker) {
            callback(url);
            return;
          }

          const signedUrl = await getSignedUrl(marker.bucket, marker.key, "GET", marker.region);
          callback(signedUrl);
          return;
        } catch (error) {
          const cleanUrl = url.split("#")[0];
          callback(cleanUrl);
          return;
        }
      }

      // Fallback: re-sign expired S3 URLs without #s3key= marker (legacy designs)
      if (url) {
        const s3Match = url.match(/^https?:\/\/([^/]+)\.s3(?:\.([^/.]+))?\.amazonaws\.com\/([^?]+)/);
        if (s3Match) {
          try {
            const { getSignedUrl } = await import("graphQL/query/file");
            const bucket = s3Match[1];
            const region = s3Match[2] || "us-east-1";
            const key = decodeURIComponent(s3Match[3]);
            const signedUrl = await getSignedUrl(bucket, key, "GET", region);
            callback(signedUrl);
            return;
          } catch (error) {
            // Use original URL as fallback
          }
        }
      }

      callback(url);
    });
}

const resolveAssetUrlDebounced = debounce(resolveAssetUrl, 500);

/**
 * @deprecated Use {@link useImageOrVideoAsset} instead.
 *
 * @public
 */
export const useAsset = useImageOrVideoAsset;

/** @public */
export function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined" || !("matchMedia" in window)) return;
    const mql = window.matchMedia("(prefers-reduced-motion: reduce)");
    const handler = () => {
      setPrefersReducedMotion(mql.matches);
    };
    handler();
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, []);

  return prefersReducedMotion;
}

function getIsAnimated(editor: Editor, shape: TLImageShape) {
  const asset = shape.props.assetId ? editor.getAsset(shape.props.assetId) : undefined;

  if (!asset) return false;

  return (
    ("mimeType" in asset.props && MediaHelpers.isAnimatedImageType(asset?.props.mimeType)) ||
    ("isAnimated" in asset.props && asset.props.isAnimated)
  );
}

function getCroppedContainerStyle(shape: TLImageShape) {
  const crop = shape.props.crop;
  const topLeft = crop?.topLeft;
  if (!topLeft) {
    return {
      width: shape.props.w,
      height: shape.props.h,
    };
  }

  const w = (1 / (crop.bottomRight.x - crop.topLeft.x)) * shape.props.w;
  const h = (1 / (crop.bottomRight.y - crop.topLeft.y)) * shape.props.h;

  const offsetX = -topLeft.x * w;
  const offsetY = -topLeft.y * h;
  return {
    transform: `translate(${offsetX}px, ${offsetY}px)`,
    width: w,
    height: h,
  };
}

function getFlipStyle(shape: TLImageShape, size?: { width: number; height: number }) {
  const { flipX, flipY } = shape.props;
  if (!flipX && !flipY) return undefined;

  const scale = `scale(${flipX ? -1 : 1}, ${flipY ? -1 : 1})`;
  const translate = size ? `translate(${flipX ? size.width : 0}px, ${flipY ? size.height : 0}px)` : "";

  return {
    transform: `${translate} ${scale}`,
    // in SVG, flipping around the center doesn't work so we use explicit width/height
    transformOrigin: size ? "0 0" : "center center",
  };
}

function SvgImage({ shape, src }: { shape: TLImageShape; src: string }) {
  const cropClipId = useUniqueSafeId();
  const containerStyle = getCroppedContainerStyle(shape);
  const crop = shape.props.crop;
  if (containerStyle.transform && crop) {
    const { transform: cropTransform, width, height } = containerStyle;
    const croppedWidth = (crop.bottomRight.x - crop.topLeft.x) * width;
    const croppedHeight = (crop.bottomRight.y - crop.topLeft.y) * height;

    const points = [
      new Vec(0, 0),
      new Vec(croppedWidth, 0),
      new Vec(croppedWidth, croppedHeight),
      new Vec(0, croppedHeight),
    ];

    const flip = getFlipStyle(shape, { width, height });

    return (
      <>
        <defs>
          <clipPath id={cropClipId}>
            <polygon points={points.map((p) => `${p.x},${p.y}`).join(" ")} />
          </clipPath>
        </defs>
        <g clipPath={`url(#${cropClipId})`}>
          <image
            href={src}
            width={width}
            height={height}
            style={flip ? { ...flip, transform: `${cropTransform} ${flip.transform}` } : { transform: cropTransform }}
          />
        </g>
      </>
    );
  } else {
    return (
      <image
        href={src}
        width={shape.props.w}
        height={shape.props.h}
        style={getFlipStyle(shape, { width: shape.props.w, height: shape.props.h })}
      />
    );
  }
}

async function getDataURIFromURL(url: string): Promise<string> {
  const response = await fetch(url);
  const blob = await response.blob();
  return FileHelpers.blobToDataUrl(blob);
}
